// @flow
import React from 'react';
import Svg from '../live-draw/svg/Svg';
import { isInRect, newId } from '../util/DOMUtil';
import AiDataPopup from '../ai/AiDataPopup';
import Authenticator from '../protocol/Authenticator';
import { AssetAiData, BoundingBox } from '../queries/metadata/AssetAiData';
import { RepoItem, GetMetadataResp, FuidRrnInfo, Point } from '../autogenerated/ZoomBridge';
import GetMetadata from '../queries/metadata/GetMetadata';
import FileInfoUtil from '../util/FileInfoUtil';
import MetadataUtil from '../util/MetadataUtil';
import AssetAiDataModel from '../queries/metadata/AssetAiDataModel';
import Store from '../store/Store';
import { SET_ACTIVE_METADATA_OBJECT, SET_LOCATE_ACTIVE_ID, SELECTED_ASSET_AI_METADATA } from '../store/Constants';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';
import { KeyCode } from '../util/Constants';

const debug = require('../debug/debug')('wc:components:WithAiData');

type State = {
  activeShapeId: ?string,
  aiData: Object[],
  showAiDataPopup: boolean,
  popUpCoordinates: Object,
  showDotted: boolean,
  resizable: boolean,
  imgLoaded: boolean,
  aiDocumentIds?: Array<string>,
  assetAiModel?: AssetAiDataModel,
  boudingBox2DocumentId?: Map<BoundingBox, string>,
  showDefaultValue: boolean,
  isFaceEyeIconEnable: boolean,
  showDimension: boolean,
  coordinates: {width: Number, height: Number}
};
type Props = {
  asset: RepoItem,
  metadataVisibiity: Set<string>,
  cropMode: boolean,
  assetScaleInfo: Object,
  height: Number,
  width: Number
};

export type Dimensions = {
  height: number,
  width: number,
  x: number,
  y: number
}

type TextProperties = {
  x: number,
  textWidth: number,
  isRight: boolean
};
const AI_TYPE = {FACE: 'face'};
const WithAiData = (getRootNode: any, getHostNode: Function, getImgNode: Function) => (
  Component: React.Component<any, any>
) => {
  return class WithAiData extends React.Component<Props, State> {
    static WrappedComponent = Component;
    _component: any;
    _width: number;
    _height: number;
    setActiveMetadataObject: any;
    activeShapesGroupNames: Set<string> = new Set();

    constructor(props: Props) {
      super(props);
      const state: State = {
        imgLoaded: false,
        activeShapeId: null,
        aiData: [],
        showAiDataPopup: false,
        popUpCoordinates: {},
        shapes: [],
        showDotted: false,
        resizable: false,
        isLocate: false,
        showDefaultValue: false,
        isFaceEyeIconEnable: false,
        showDimension: false,
        assetAiModel: null,
        aiDocumentIds: null,
        coordinates: {height: 0, width: 0}
      };
      this.state = state;
    }

    componentDidMount() {
      Store.put(SET_LOCATE_ACTIVE_ID, this.setLocateActiveId);
      this.addEventListner();
    }

    setCoordinates = (assetScaleInfo) => {
      const {height, width} = this.props;
      const coordinates = {
        width: Math.round(width * assetScaleInfo.scale)-1,
        height: Math.round(height * assetScaleInfo.scale)
      };     
      this.setState({coordinates});
    }

    componentWillUnmount() {
      this.removeEventListner()
    }

    addEventListner = () =>{
      KeyboardShortcuts.add(new KeydownConfig('ASSET_AI_METADATA', this.showDefaultFaceMetadata, [KeyCode.F],[1]))
      KeyboardShortcuts.add(new KeydownConfig('ASSET_AI_METADATA', this.showFaceBoxDimensions, [KeyCode.D],[1]))
    }

    showDefaultFaceMetadata = (e: KeyboardEvent) => {
      if(e.shiftKey) {
        const {isFaceEyeIconEnable, showDefaultValue} = this.state;
        if(e.shiftKey && isFaceEyeIconEnable){
          this.setState({showDefaultValue: !showDefaultValue},()=> {
            this.updateSvgImage()});
        }
      }
    }

    showFaceBoxDimensions = (e: KeyboardEvent) => {
      const {showDimension, isFaceEyeIconEnable} = this.state;
      if(e.shiftKey && isFaceEyeIconEnable) {
        this.setState({showDimension: !showDimension}, ()=> this.updateSvgImage())
      }
    }

    removeEventListner = () => {
      KeyboardShortcuts.remove('ASSET_AI_METADATA')
    }


    componentWillReceiveProps(nextProps) {
      const { coordinates } = this.state;
      const { height, width } = coordinates;
      const metadataVisibiity: Set<string> = nextProps.metadataVisibiity;
      this.getPointsForSvgImage(metadataVisibiity);
      this.setState({showDefaultValue: false})
      this.activeShapesGroupNames = new Set([...metadataVisibiity]);
      this.setAIdataIfNotExit();
      if(nextProps.assetScaleInfo.scale !== this.props.assetScaleInfo.scale || nextProps.width !== width || nextProps.height !== height) {
        this.setCoordinates(nextProps.assetScaleInfo);
      }
      if (!this.setActiveMetadataObject) {
        this.setActiveMetadataObject = Store.get(SET_ACTIVE_METADATA_OBJECT);
      }
    }

    updateSvgImage = () => {
      this.getPointsForSvgImage();
    };

    setAIdataIfNotExit = () => {
      const { assetAiData } = this.state;
      if (this.state.imgLoaded && !assetAiData) {
        const assetAiData: AssetAiDataModel = Store.get(SELECTED_ASSET_AI_METADATA);
        if (assetAiData) {
          this.setState(
            {
              assetAiModel: assetAiData,
              aiDocumentIds: assetAiData.getAllDocumentIds()
            },
            this.getPointsForSvgImage
          );
        } else {
          this.setState({
            assetAiModel: null,
            aiDocumentIds: null
          })
        }
      }
    };


    calculateSvgPoints = (inputPoints: Array<Point>, imgNode: Object): Array<number> => {
      const {coordinates} = this.state;
      const points = [];
      for (let index = 0; index < inputPoints.length; index++) {
        const p: Point = inputPoints[index];
        let xPoint: number = coordinates.width * p.getX();
        let yPoint: number = coordinates.height * p.getY();
        if (xPoint < 0) {
          xPoint = 0;
        }
        if (yPoint < 0) {
          yPoint = 0;
        }
        if (xPoint > coordinates.width) {
          xPoint = coordinates.width;
        }
        if (yPoint > coordinates.height) {
          yPoint = coordinates.height;
        }
        points.push(xPoint);
        points.push(yPoint);
      }
      return points;
    };

    sortShapesOrder = (shapes: Array<any>) => {
      if (shapes.length > 1) shapes.sort(areaCompare);
      this.setState({ shapes });
    };

    getPointsForSvgImage = (metadataVisibiity: Set<string> = []) => {
      const activeVisibileGroups: Set<string> = metadataVisibiity.size
        ? metadataVisibiity
        : this.props.metadataVisibiity;
      const imgNode: Object = getImgNode(this._component).getBoundingClientRect();
      const boudingBox2DocumentId: Map<BoundingBox, string> = new Map();
      const { activeShapeId, isLocate } = this.state;
      if (this.state.aiDocumentIds && this.state.assetAiModel) {
        for (let index = 0; index < this.state.aiDocumentIds.length; index++) {
          const documentId: string = this.state.aiDocumentIds[index];
          //$FlowFixMe
          const assetAiData: AssetAiData = this.state.assetAiModel.getAssetAiData(documentId);
          const boxes: Array<BoundingBox> = assetAiData.getBoundingBox();
          if (activeVisibileGroups.size) {
            if (activeVisibileGroups.has(documentId)) {
              setboudingBox2DocumentId(boxes, boudingBox2DocumentId, assetAiData.getUuid());
            }
          }
          if (isLocate && activeShapeId === documentId) {
            setboudingBox2DocumentId(boxes, boudingBox2DocumentId, assetAiData.getUuid());
          }
          //Comment for show no AI group on image till Click on Eye Icon
          // else {
          //   setboudingBox2DocumentId(boxes, boudingBox2DocumentId, assetAiData.getUuid());
          // }
        }
      }

      const shapes: Array<any> = [];
      let isExit = false;
      if (boudingBox2DocumentId.size > 0) {
        let index = 0;
        boudingBox2DocumentId.forEach((documentId: string, key: BoundingBox) => {
          const assetAiData1: AssetAiData = this.state.assetAiModel.getAssetAiData(documentId);
          if (activeShapeId && documentId === activeShapeId) isExit = true;
          const points: Array<Point> = key.getPoints();
          const outPoints: Array<number> = this.calculateSvgPoints(points, imgNode);
          const x: number = outPoints[0];
          const y: number = outPoints[1];
          if(assetAiData1.getAiType() === AI_TYPE.FACE) {
            this.setState({isFaceEyeIconEnable: true})
          } else {
            this.setState({isFaceEyeIconEnable: false})
          }
          if (outPoints.length > 0) {
            const value: string = this.getValueOfBox(assetAiData1, index+1)
            const data = this.getSvgData(outPoints, value, x, y, documentId, index, imgNode);
            if (data) shapes.push(data);
          }
          index++;
        });
      }
      if (!isExit) {
        this.setState({ activeShapeId: null });
      }

      this.sortShapesOrder(shapes);
    };

    getValueOfBox = (assetAiData: AssetAiData, index: number) => {
      const {showDefaultValue} = this.state;
      if(assetAiData.getValue())
      return assetAiData.getValue();
      else if(showDefaultValue && assetAiData.getAiType() === AI_TYPE.FACE)
      return `${assetAiData.getAiType()}  ${index}`;
      else return ''
    }

    calcualteDistance(points: Array<number>): Array<number> {
      const a = points[0] - points[2];
      const b = points[1] - points[3];
      const c = points[0] - points[6];
      const d = points[1] - points[7];
      return [Math.sqrt(a * a + b * b), Math.sqrt(c * c + d * d)];
    }

    getSvgData = (
      points: Array<number>,
      value: string,
      x: number,
      y: number,
      documentId: string,
      index: number,
      imgNode: Object
    ): Object => {
      const {coordinates} = this.state;
      const widthHight = this.calcualteDistance(points);
      const shape = {
        ...this.getNewShapeData(),
        type: 'textsvg',
        points: points,
        value: value,
        dimensions: calculateDimentionPosiotion(x,y, widthHight[0], widthHight[0], imgNode, coordinates),
        showDimension: this.state.showDimension,
        x: x,
        y: y,
        id: documentId,
        width: widthHight[0],
        height: widthHight[1],
        strockColor: '#fff',
        Ukey: documentId + index,
        textProps: calculateTextPostion(x, value, imgNode, coordinates)
      };
      return shape;
    };

    getNewShapeData = () => {
      return {
        //id: newId(),
        creator: Authenticator.AUTH_DATA.username,
        stroke: '#685ceb',
        strokeWidth: 2,
        fill: 'transparent'
      };
    };

    /*  getAiData = async () => {
      const asset: RepoItem = this.props.asset;
      try {
        if (this.state.imgLoaded) {
          const fuiRrnInfo: FuidRrnInfo = FileInfoUtil.createFuidRrnInfo(asset);
          const getmetadataQuery: GetMetadata = new GetMetadata([fuiRrnInfo], false);

          const res: GetMetadataResp = await getmetadataQuery.run();
          const assetAiData: AssetAiDataModel = MetadataUtil.getAiDataModelFromMetadataResponse(res, fuiRrnInfo);
          if (assetAiData) {
            this.setState(
              {
                assetAiModel: assetAiData,
                aiDocumentIds: assetAiData.getAllDocumentIds()
              },
              this.getPointsForSvgImage
            );
          }
        }
      } catch (err) {
        console.error(err);
        debug('[getAiData()] error: ', err);
      }
    }; */

    onUpdateShape = (shape) => {
      debug('[onUpdateShape()]');
      this._ignoreClickEvent = true;
      setTimeout(() => (this._ignoreClickEvent = false), 100);
      const newAiData: [] = this.getAiDataIndex();
      this.setState({
        aiData: newAiData.concat(shape)
      });
    };

    setShapeAndCallgetSvg = (activeShapeId, isLocate) => {
      this.setState({ activeShapeId, isLocate }, () => {
        this.getPointsForSvgImage(this.activeShapesGroupNames);
      });
    };

    setActiveShapeId = (activeShapeId: string, groupName: string = null) => {
      this.setAIdataIfNotExit();
      if (groupName && !this.activeShapesGroupNames.has(groupName)) {
        if (activeShapeId === this.state.activeShapeId) this.setShapeAndCallgetSvg(null, false);
        else this.setShapeAndCallgetSvg(activeShapeId, true);
      } else if (activeShapeId === this.state.activeShapeId) {
        // this.setShapeAndCallgetSvg(null, false);
        this.setState({ activeShapeId: null, isLocate: false });
      } else {
        this.setShapeAndCallgetSvg(activeShapeId, false);
        //this.setState({ activeShapeId, isLocate: false });
      }
    };

    setLocateActiveId = (activeShapeId, groupName) => {
      this.setActiveShapeId(activeShapeId, groupName);
    };

    calculateHeightOfSvg = (points: string): number => {
      const pointsArr: [] = points.split(',');
      if (pointsArr.length > 0) {
        return pointsArr[7] - pointsArr[1];
      }
    };

    calculateWidthOfSvg = (points: string): number => {
      const pointsArr: [] = points.split(',');
      if (pointsArr.length > 0) {
        return pointsArr[2] - pointsArr[0];
      }
    };

    onSvgClick = (event: Event, data: Object) => {
      if (this.setActiveMetadataObject) {
        this.setActiveMetadataObject(data.id);
      }
    };

    onCreateShape = async (shape) => {
      let nextState = {
        activeActiveId: shape.id
      };

      if (this.state.freehand) {
        nextState.freehand = false;
      }

      this.setState(nextState);
      // this.props.liveDraw.onCreateShape(shape);
    };

    getAiDataIndex(): number {
      const { aiData, activeShapeId } = this.state;
      const ind = aiData.findIndex((data) => {
        return data.id === activeShapeId;
      });
      if (ind !== -1) {
        aiData.splice(ind, 1);
      }
      return aiData;
    }

    onRef = (component: any) => {
      this._component = component ? component._component : null;
    };

    init = () => {
      this._shapeIdToThreadId = {};
      this._ignoreClickEvent = false;
    };

    onClose = () => {
      this.setState({
        showAiDataPopup: false,
        showDotted: false
      });
    };

    onImageLoad = () => {
      this.setState({
        imgLoaded: true
      }, ()=>{
        this.setAIdataIfNotExit()
      });
    };

    isTextDrawingMode = () => {
      if (this._component) return this._component.textDrawingMode;
    };

    render() {
      const { activeShapeId, showAiDataPopup, shapes, popUpCoordinates, showDotted, resizable } = this.state;
      const svgProps = {
        data: shapes,
        hostNode: getHostNode(this._component),
        freehand: false,
        scale: 1,
        activeShapeId,
        getNewShapeData: this.getNewShapeData,
        setActiveShapeId: this.setActiveShapeId,
        onCreateShape: this.onCreateShape,
        onUpdateShape: this.onUpdateShape,
        onClick: this.onSvgClick,
        showDotted,
        resizable: false
      };
      const { cropMode } = this.props;
      const props = {
        ...this.props,
        onImageLoaded: this.onImageLoad,
        updateSvgImage: this.updateSvgImage
      };
      return (
        <Component {...props} ref={this.onRef}>
          {cropMode || this.isTextDrawingMode() ? null : <Svg {...svgProps} />}
          {showAiDataPopup ? (
            <AiDataPopup
              style={{ bottom: window.innerHeight - popUpCoordinates.top + 30, left: popUpCoordinates.left }}
              onClose={this.onClose}
            />
          ) : null}
        </Component>
      );
    }
  };
};
export default WithAiData;

function setboudingBox2DocumentId(boxes: Array<BoundingBox>, boudingBox2DocumentId: Map<BoundingBox, string>, Uuid) {
  if (boxes) {
    for (let bIndex = 0; bIndex < boxes.length; bIndex++) {
      const box: BoundingBox = boxes[bIndex];
      boudingBox2DocumentId.set(box, Uuid);
    }
  }
}

//$FlowFixMe
function areaCompare(a, b) {
  const a1 = a.width * a.height;
  const b1 = b.width * b.height;
  return a1 > b1 ? -1 : b1 > a1 ? 1 : 0;
}

function calculateTextPostion(x: number, text: string, imgNode: Object, coordinates: object): TextProperties {
  const lengthOfValue = text ? text.length : 0;
  const textWidth = lengthOfValue * 10;
  const posOfTextRect = x + textWidth;
  let diff = 0;
  if (posOfTextRect > coordinates.width) {
    diff = posOfTextRect - coordinates.width;
  }
  if (diff > 0) {
    return { x: x - diff, textWidth, isRight: true };
  }
  return { x: x, textWidth, isRight: false };
}

function calculateDimentionPosiotion(x, y, width, height, imgNode: object, coordinates: object): Dimensions {
  const dimensions: Dimensions = {};
  const textWidth = 150;
  const posOfTextRect = x + textWidth;
  dimensions.x = x
  let diff = 0;
  if(posOfTextRect > coordinates.width) {
    diff = posOfTextRect - coordinates.width;
  }
  if(diff > 0) {
    dimensions.x = x - diff;
  }
  dimensions.height = height;
  dimensions.width = width;
  dimensions.y = y;
  return dimensions;
}